import ErrorIcon from "@servicetitan/hammer-icon/mdi/round/error.svg";
import WarningIcon from "@servicetitan/hammer-icon/mdi/round/warning.svg";
import { Icon } from "../../../../../../components/Icon";
import styles from "./DataTableCells.module.scss";
import { Tooltip } from "../../../../../../components";
import { useCellFocusContext } from "./CellFocusContext";
import { useState } from "react";

export type CellStatusProps = {
  /** Status value - true for status without message, string for status with accessible message */
  status: boolean | string;
  /** The variant determines the icon, color, and aria-label */
  variant: "error" | "warning";
};

const variantConfig = {
  error: {
    icon: ErrorIcon,
    className: "cell-error-icon",
    label: "Cell has an error",
  },
  warning: {
    icon: WarningIcon,
    className: "cell-warning-icon",
    label: "Cell has a warning",
  },
} as const;

/**
 * Renders a status icon (error or warning) for cells.
 * When status is a string, it's used as the aria-label for accessibility and shown in a tooltip.
 * When the parent cell is focused, the tooltip is displayed for keyboard accessibility.
 */
export function CellStatus({ status, variant }: CellStatusProps) {
  const message = typeof status === "string" ? status : undefined;
  const cellFocusContext = useCellFocusContext();
  const [isHovered, setIsHovered] = useState(false);
  const config = variantConfig[variant];

  const icon = (
    <Icon
      svg={config.icon}
      className={styles[config.className]}
      aria-label={message ? `${config.label}: ${message}` : `${config.label}.`}
    />
  );

  if (!message) {
    return icon;
  }

  // When the cell is focused via keyboard, force the tooltip open, otherwise, open on hover.
  const isTooltipOpen = cellFocusContext?.isCellFocused || isHovered;

  return (
    <Tooltip open={isTooltipOpen}>
      {/* We have to track hover manually because the tooltip doesn't like being conditionally controlled */}
      <Tooltip.Trigger
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {icon}
      </Tooltip.Trigger>
      <Tooltip.Content>{message}</Tooltip.Content>
    </Tooltip>
  );
}
